import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Box,
  Modal,
  Typography,
  Card,
  CardContent,
  ToggleButton,
} from "@mui/material";
import GCTitleSticky from "../../../Component/Common/ReuseableComponent/GCTitleSticky";
import { SegregationLocation_Pending_Detail } from "../../../Server/ApiV2";
import { SegregationRo } from "../../../Redux/roStore";
import { CmpStatusIconV3 } from "../../../Component/Common/ToolBox/ToolBox";
import FilterAltIcon from "@mui/icons-material/FilterAlt"; // Import filter icon

const ViewList = ({
  open = false,
  fCloseSearchModal = () => {},
  xTitle = "Location Wise",
}) => {
  const [viewData, setViewData] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Function to group data by Location
  function groupByLocation(data) {
    const uniqueLocations = [...new Set(data.map((item) => item.Location))];
    return uniqueLocations.map((location) => {
      const subdata = data.filter((item) => item.Location === location);
      const locationName = subdata[0]?.LocName || "Unknown";
      return { Location: location, LocationName: locationName, subdata };
    });
  }

  // Fetch data from API and process it
  const handleLoad = async () => {
    const { Head } = SegregationRo();
    const obj = { VocTyp: Head.A_VocTyp, VocNo: Head.A_VocNo };
    const result = await SegregationLocation_Pending_Detail(obj);

    if (result.status === 200) {
      const updatedData = result?.data?.ResultSet?.map((item) => {
        let indicator;
        if (item.BalanceQty === 0) {
          indicator = 3; // Green
        } else if (item.TotalPickedQty > 0) {
          indicator = 1; // Yellow
        } else {
          indicator = 2; // Red
        }
        return { ...item, Indicator: indicator };
      })?.sort((a, b) => a.Indicator - b.Indicator);

      const groupedData = groupByLocation(updatedData);
      setViewData(groupedData);
      setSelectedLocations(groupedData.map((group) => group.Location)); // Select all locations by default
    }
  };

  useEffect(() => {
    if (open) handleLoad();
  }, [open]);

  // Handle location filter toggle
  const handleLocationClick = (location) => {
    setSelectedLocations(
      (prev) =>
        prev.includes(location)
          ? prev.filter((loc) => loc !== location) // Remove if already selected
          : [...prev, location] // Add new location
    );
  };

  // Select or Deselect all locations toggle
  const handleSelectDeselectAll = () => {
    if (selectedLocations.length === viewData.length) {
      setSelectedLocations([]); // Deselect all -> Show no data
    } else {
      setSelectedLocations(viewData.map((group) => group.Location)); // Select all locations
    }
  };

  // **Ensure filteredData is empty when all selections are removed**
  const filteredData =
    selectedLocations.length > 0
      ? viewData.filter((group) => selectedLocations.includes(group.Location))
      : [];

  return (
    <Modal
      open={open}
      onClose={() => {
        fCloseSearchModal();
        setViewData([]);
        setSelectedLocations([]);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="container-fluid">
        <Box sx={{ overflow: "auto" }} className="model-GCSearchBtn">
          <GCTitleSticky
            title={xTitle}
            onClose={() => {
              fCloseSearchModal();
              setViewData([]);
              setSelectedLocations([]);
            }}
          />

          {/* Location Filter Buttons */}
          <Box sx={{ display: "flex", flexWrap: "wrap", p: 1, gap: 1 }}>
            {/* Select/Deselect Toggle Button */}
            <ToggleButton
              value="check"
              selected={selectedLocations.length === viewData.length}
              onChange={handleSelectDeselectAll}
              sx={{
                fontSize: "10px",
                padding: "1px 5px",
                borderRadius: "20px",
                backgroundColor:
                  selectedLocations.length === viewData.length
                    ? "#1976d2"
                    : "#E0E0E0",
                color:
                  selectedLocations.length === viewData.length
                    ? "#fff"
                    : "#000",
                "&:hover": {
                  backgroundColor:
                    selectedLocations.length === viewData.length
                      ? "#1565c0"
                      : "#BDBDBD",
                },
              }}
            >
              {selectedLocations.length === viewData.length
                ? "Deselect All"
                : "Select All"}
            </ToggleButton>

            {viewData.map((group) => (
              <Button
                key={group.Location}
                variant="contained"
                onClick={() => handleLocationClick(group.Location)}
                sx={{
                  fontSize: "10px",
                  padding: "1px 5px",
                  minWidth: "100px",
                  backgroundColor: selectedLocations.includes(group.Location)
                    ? "#1976d2"
                    : "#E0E0E0",
                  color: selectedLocations.includes(group.Location)
                    ? "#fff"
                    : "#000",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  boxShadow: selectedLocations.includes(group.Location)
                    ? "2px 2px 5px rgba(0, 0, 0, 0.2)"
                    : "none",
                  "&:hover": {
                    backgroundColor: selectedLocations.includes(group.Location)
                      ? "#1565c0"
                      : "#BDBDBD",
                  },
                }}
              >
                {selectedLocations.includes(group.Location) && (
                  <FilterAltIcon fontSize="small" />
                )}
                {group.LocationName} ({group.Location})
              </Button>
            ))}
          </Box>

          {/* Display Filtered Data */}
          {filteredData.length > 0 ? (
            <Box m={1}>
              {filteredData.map((group, groupIndex) => (
                <Box key={groupIndex} mb={3}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {group?.LocationName}
                  </Typography>

                  <Grid container spacing={1}>
                    {group?.subdata?.map((row, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card
                          sx={{
                            width: "100%",
                            borderRadius: 3,
                            boxShadow: 2,
                            border: "1px solid #FAF9F6",
                            "& .MuiCardContent-root": {
                              padding: "5px !important",
                            },
                          }}
                        >
                          <CardContent sx={{ padding: "5px" }}>
                            <Box sx={{ backgroundColor: "#FAF9F6" }}>
                              <Grid container alignItems={"center"}>
                                <Grid item xs={11}>
                                  <Typography
                                    variant="span"
                                    fontWeight="bold"
                                    noWrap
                                    sx={{ 
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "block",
                                    }}
                                  >
                                    {row.ItemName}
                                  </Typography>
                                </Grid>
                                <Grid item xs={1} textAlign="right">
                                  <CmpStatusIconV3 xStatus={row.Indicator} />
                                </Grid>
                              </Grid>
                            </Box>
                            <Box
                              sx={{
                                backgroundColor: "#F4F4F4",
                                borderRadius: 2,
                                paddingTop: "5px",
                                mt: 0.5,
                              }}
                            >
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <Typography variant="caption" color="gray">
                                    Qty
                                  </Typography>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    {row.TotalBaseQty}
                                  </Typography>
                                </Grid>
                                <Grid item xs={5}>
                                  <Typography variant="caption" color="gray">
                                    Seg.Qty
                                  </Typography>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    {row.TotalPickedQty}
                                  </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                  <Typography variant="caption" color="gray">
                                    Balance
                                  </Typography>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    {row.BalanceQty}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography align="center" sx={{ mt: 3, fontSize: "16px" }}>
              No Data Available
            </Typography>
          )}
        </Box>
      </div>
    </Modal>
  );
};

export default ViewList;
